import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function VerifyEmailPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuthStore();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');

    const token = searchParams.get('token');

    const verifyMutation = useMutation({
        mutationFn: (token: string) => api.verifyEmail(token),
        onSuccess: (data) => {
            setStatus('success');
            // Auto login after verification
            if (data.user && data.workspace && data.access_token) {
                login({ ...data.user, avatar_url: null }, data.workspace, data.access_token);
                // Redirect to dashboard after 2 seconds
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            }
        },
        onError: (error: Error) => {
            setStatus('error');
            setErrorMessage(error.message || 'Verification failed');
        },
    });

    useEffect(() => {
        if (token) {
            verifyMutation.mutate(token);
        } else {
            setStatus('error');
            setErrorMessage('No verification token provided');
        }
    }, [token]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-neutral-800 rounded-2xl shadow-xl p-8 text-center">
                {status === 'loading' && (
                    <>
                        <Loader2 className="w-16 h-16 text-primary-500 mx-auto mb-4 animate-spin" />
                        <h1 className="text-2xl font-bold mb-2">
                            {t('auth.verifyingEmail', 'Verifying your email...')}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400">
                            {t('auth.pleaseWait', 'Please wait a moment')}
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckCircle2 className="w-16 h-16 text-success-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold mb-2 text-success-600">
                            {t('auth.emailVerified', 'Email Verified!')}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                            {t('auth.emailVerifiedDesc', 'Your email has been verified successfully. Redirecting to dashboard...')}
                        </p>
                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary-500" />
                    </>
                )}

                {status === 'error' && (
                    <>
                        <XCircle className="w-16 h-16 text-danger-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold mb-2 text-danger-600">
                            {t('auth.verificationFailed', 'Verification Failed')}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                            {errorMessage}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-primary w-full"
                            >
                                {t('auth.goToLogin', 'Go to Login')}
                            </button>
                            <button
                                onClick={() => navigate('/register')}
                                className="btn-secondary w-full"
                            >
                                {t('auth.registerAgain', 'Register Again')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
